import { CartLinesUpdateEvent } from '@shopify/events';

const money = (cents) => {
  const currency = window.Shopify?.currency?.active || 'EUR';
  try { return new Intl.NumberFormat(document.documentElement.lang || navigator.language, { style: 'currency', currency }).format(Number(cents || 0) / 100); }
  catch { return String(cents || 0); }
};
const imageSource = (image) => typeof image === 'string' ? image : image?.src || image?.url || '';
const normalizedOption = (value) => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').trim().toLocaleLowerCase();
const commonColors = [
  ['black', 'nero', 'noir', 'schwarz', 'negro', 'preto', 'zwart', 'sort', 'svart', 'أسود', 'اسود'],
  ['gray', 'grey', 'grigio', 'gris', 'grau', 'cinza', 'grijs', 'gra', 'grå', 'رمادي'],
  ['white', 'bianco', 'blanc', 'weiß', 'weiss', 'blanco', 'branco', 'wit', 'hvid', 'vit', 'أبيض', 'ابيض'],
  ['blue', 'blu', 'bleu', 'blau', 'azul', 'blauw', 'bla', 'blå', 'أزرق', 'ازرق'],
  ['brown', 'marrone', 'brun', 'braun', 'marron', 'castano', 'castanho', 'bruin', 'brun', 'brunt', 'بني']
].map((names) => new Set(names.map(normalizedOption)));
const colorKey = (value) => {
  const key = normalizedOption(value).replace(/\s+/g, ' ');
  const group = commonColors.findIndex((names) => names.has(key));
  return group < 0 ? key : `common:${group}`;
};
const visualOptionNames = new Set('color colors colour colours colore colori couleur couleurs farbe farben cor cores kolor kleur farg farve farge farger'.split(' '));
['culoare','culori','couleur','couleurs','kolory','barva','barvy','szin','renk','renkler','цвет','цвета','колір','колори','χρώμα','χρώματα','لون','ألوان','اللون','色','颜色','顏色','색상','색깔','style','styles','stile','stili','estilo','estilos','stil','stilar','stilart','modello','model','pattern','motif','disegno','print','design'].forEach((name) => visualOptionNames.add(normalizedOption(name)));
const sizeOptionNames = new Set(['size','sizes','taglia','taglie','taille','tailles','größe','groesse','grosse','talla','tallas','tamanho','tamanhos','maat','maten','storlek','storlekar','størrelse','størrelser','storrelse','storrelser','størkelse','rozmiar','rozmiary','размер','розмір','サイズ','尺码','尺寸','사이즈','مقاس','المقاس','قياس'].map(normalizedOption));
function optionKind(name, index) {
  const key = normalizedOption(name);
  if (key === 'title' || key === 'default title') return null;
  if (sizeOptionNames.has(key)) return 'size';
  if (visualOptionNames.has(key)) return 'color';
  return index === 0 ? 'color' : null;
}

function init(root) {
  if (root.dataset.rfcReady) return;
  root.dataset.rfcReady = 'true';
  const extraVisualNames = new Set(String(root.dataset.rfcVisualNames || '').split(',').map(normalizedOption).filter(Boolean));
  const priorityKeys = [...new Set(String(root.dataset.rfcColorPriority || 'Black, Gray, White, Blue, Brown').split(',').map(colorKey).filter(Boolean))];
  const colorPriority = (value) => priorityKeys.indexOf(colorKey(value));
  const cardOptionKind = (name, index) => extraVisualNames.has(normalizedOption(name)) ? 'color' : optionKind(name, index);
  const quick = root.querySelector('.rfc__quick-dialog');
  let current = null;
  let chosen = [];
  let busy = false;
  const categoryRail = root.querySelector('.rfc__categories');
  const categoryNext = root.querySelector('[data-rfc-categories-next]');
  if (categoryRail && categoryNext) {
    const updateCategories = () => { categoryNext.hidden = categoryRail.scrollWidth - categoryRail.clientWidth - categoryRail.scrollLeft < 8; };
    categoryNext.addEventListener('click', () => categoryRail.scrollBy({ left: Math.max(categoryRail.clientWidth * .75, 220), behavior: 'smooth' }));
    categoryRail.addEventListener('scroll', updateCategories, { passive: true });
    window.addEventListener('resize', updateCategories, { passive: true });
    updateCategories();
  }

  const grid = root.querySelector('.rfc__grid');
  const variantGroups = [...root.querySelectorAll('[data-rfc-variant-group]')];
  let collectionCards;
  let collectionCardsPromise;
  let collectionLoadFailed = false;
  const selectedFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return {
      color: root.querySelector('[data-rfc-variant-group="color"]') ? params.getAll('rfc_color') : [],
      size: root.querySelector('[data-rfc-variant-group="size"]') ? params.getAll('rfc_size') : []
    };
  };
  const uniqueKey = (value, kind = 'color') => kind === 'color' ? colorKey(value) : normalizedOption(value).replace(/\s+/g, ' ');
  const allCards = async () => {
    if (collectionCards) return collectionCards;
    if (collectionCardsPromise) return collectionCardsPromise;
    collectionCardsPromise = (async () => {
      const collect = (scope) => [...scope.querySelectorAll('.rfc-card[data-rfc-product]')].map((card) => card.cloneNode(true));
      const pages = Number(grid?.dataset.rfcPages) || 1;
      const current = Math.max(1, Number(grid?.dataset.rfcPage) || 1);
      const pageCards = Array.from({ length: pages }, () => []);
      pageCards[current - 1] = collect(grid);
      const requests = [];
      for (let page = 1; page <= pages; page++) if (page !== current) requests.push(page);
      for (let offset = 0; offset < requests.length; offset += 4) {
        await Promise.all(requests.slice(offset, offset + 4).map(async (page) => {
          const url = new URL(location.href);
          url.searchParams.delete('rfc_color');
          url.searchParams.delete('rfc_size');
          url.searchParams.set('page', String(page));
          let response = await fetch(url, { headers: { Accept: 'text/html' } });
          let doc = response.ok ? new DOMParser().parseFromString(await response.text(), 'text/html') : null;
          let pageGrid = doc?.querySelector(`#${CSS.escape(grid.id)}`);
          let found = pageGrid?.dataset.rfcPage === String(page) ? collect(pageGrid) : [];
          if (!found.length) {
            url.searchParams.set('section_id', root.dataset.rfcSectionId);
            response = await fetch(url, { headers: { Accept: 'text/html' } });
            doc = response.ok ? new DOMParser().parseFromString(await response.text(), 'text/html') : null;
            pageGrid = doc?.querySelector('.rfc__grid');
            found = pageGrid?.dataset.rfcPage === String(page) ? collect(pageGrid) : [];
          }
          pageCards[page - 1] = found;
          if (!pageCards[page - 1].length) throw new Error(`Collection page ${page} contained no product cards`);
        }));
      }
      const cardMap = new Map();
      pageCards.flat().forEach((card) => cardMap.set(card.dataset.rfcProduct, card));
      collectionCards = [...cardMap.values()];
      return collectionCards;
    })().catch(() => {
      collectionLoadFailed = true;
      collectionCardsPromise = null;
      return [];
    });
    return collectionCardsPromise;
  };
  const cardOptions = (card) => {
    try { return JSON.parse(card.querySelector('.rfc-card__filter-data')?.textContent || '{}'); }
    catch { return {}; }
  };
  const selectedVariant = (card, selected) => {
    const data = cardOptions(card);
    const matches = (data.variants || []).filter((variant) => ['color', 'size'].every((kind) => {
      if (!selected[kind].length) return true;
      return (data.options || []).some((option, index) => cardOptionKind(option.name, index) === kind && selected[kind].some((choice) => uniqueKey(choice, kind) === uniqueKey(variant.options?.[index], kind)));
    }));
    return matches.find((variant) => variant.available && variant.image) || matches.find((variant) => variant.available) || matches[0] || null;
  };
  const showVariant = (card, variant, selected) => {
    if (!variant) return;
    const productUrl = card.dataset.rfcProductUrl;
    if (productUrl && variant.id) {
      const url = new URL(productUrl, location.origin);
      url.searchParams.set('variant', String(variant.id));
      card.querySelectorAll('.rfc-card__media,.rfc-card__title').forEach((link) => { link.href = url.href; });
    }
    const image = card.querySelector('.rfc-card__image');
    if (image instanceof HTMLImageElement && variant.image) {
      image.src = variant.image;
      image.removeAttribute('srcset');
    }
    const price = card.querySelector('.rfc-card__price');
    if (price && Number.isFinite(Number(variant.price))) {
      const compare = Number(variant.compare_at_price || 0);
      const current = Number(variant.price);
      let old = price.querySelector('s');
      if (compare > current) {
        if (!old) { old = document.createElement('s'); price.prepend(old); }
        old.textContent = money(compare);
      } else old?.remove();
      const currentNode = price.querySelector('strong');
      if (currentNode) currentNode.textContent = money(current);
      let badge = card.querySelector('.rfc-card__badge');
      if (card.dataset.rfcSoldOut !== 'true' && card.dataset.rfcBadgeAllowed === 'true' && card.dataset.rfcBadgeMode !== 'off') {
        if (compare > current && !badge) {
          badge = document.createElement('span');
          badge.className = 'rfc-card__badge';
          card.querySelector('.rfc-card__media')?.append(badge);
        }
        if (badge) {
          badge.hidden = compare <= current;
          if (compare > current) badge.textContent = card.dataset.rfcBadgeMode === 'percent' ? `−${Math.round((compare - current) * 100 / compare)}%` : (card.dataset.rfcBadgeLabel || 'Sale');
        }
      }
    }
    if (selected.color.length) {
      const data = cardOptions(card);
      const colorIndex = (data.options || []).findIndex((option, index) => cardOptionKind(option.name, index) === 'color');
      const value = variant.options?.[colorIndex];
      card.querySelectorAll('[data-rfc-swatch]').forEach((swatch) => swatch.classList.toggle('is-active', colorKey(swatch.title) === colorKey(value)));
    }
  };
  const populateVariantGroups = async () => {
    if (!variantGroups.length) return;
    variantGroups.forEach((group) => { group.querySelector('[data-rfc-variant-values]').textContent = root.dataset.rfcLoadingLabel || 'Loading options…'; });
    const cards = await allCards();
    if (collectionLoadFailed) {
      variantGroups.forEach((group) => { group.open = true; group.querySelector('[data-rfc-variant-values]').textContent = root.dataset.rfcLoadError || 'Could not load all options. Please try again.'; });
      collectionLoadFailed = false;
      return;
    }
    const selected = selectedFromUrl();
    const values = { color: new Map(), size: new Map() };
    cards.forEach((card) => {
      const data = cardOptions(card);
      (data.options || []).forEach((option, index) => {
        const kind = cardOptionKind(option.name, index);
        if (!kind) return;
        (data.variants || []).forEach((variant) => {
          const name = String(variant.options?.[index] || '').trim();
          if (name && !values[kind].has(uniqueKey(name, kind))) values[kind].set(uniqueKey(name, kind), name);
        });
      });
    });
    variantGroups.forEach((group) => {
      const kind = group.dataset.rfcVariantGroup;
      const area = group.querySelector('[data-rfc-variant-values]');
      const list = [...values[kind].values()].sort((a, b) => {
        if (kind === 'color') {
          const first = colorPriority(a), second = colorPriority(b);
          if (first >= 0 || second >= 0) return (first < 0 ? 99 : first) - (second < 0 ? 99 : second);
        }
        return a.localeCompare(b, document.documentElement.lang || undefined, { numeric: true, sensitivity: 'base' });
      });
      group.hidden = !list.length;
      group.open = selected[kind].length > 0;
      area.replaceChildren();
      list.forEach((value) => {
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox'; input.name = `rfc_${kind}`; input.value = value;
        input.checked = selected[kind].some((chosen) => uniqueKey(chosen, kind) === uniqueKey(value, kind));
        const span = document.createElement('span'); span.textContent = value;
        label.append(input, span); area.append(label);
      });
    });
    if (selected.color.length || selected.size.length) {
      const matches = cards.map((card) => ({ card, variant: selectedVariant(card, selected) })).filter((entry) => entry.variant);
      grid.replaceChildren(...matches.map(({ card, variant }) => {
        const result = card.cloneNode(true);
        showVariant(result, variant, selected);
        return result;
      }));
      if (!matches.length) {
        const empty = document.createElement('p'); empty.className = 'rfc__empty';
        empty.textContent = root.dataset.rfcEmptyLabel || 'No products match these filters.';
        grid.append(empty);
      }
      root.querySelector('.rfc__pagination')?.setAttribute('hidden', '');
    }
  };
  if (variantGroups.length && grid) {
    root.querySelector('[data-rfc-open]')?.addEventListener('click', () => { populateVariantGroups(); });
    if (selectedFromUrl().color.length || selectedFromUrl().size.length) populateVariantGroups();
  }

  root.querySelector('.rfc__filter-form')?.addEventListener('change', (event) => {
    const changed = event.target;
    if (!changed?.matches?.('[data-rfc-color-filter]') || !changed.checked) return;
    root.querySelectorAll('[data-rfc-color-filter]:checked').forEach((input) => {
      if (input !== changed && input.name !== changed.name) input.checked = false;
    });
  });

  root.querySelectorAll('[data-rfc-price-range]').forEach((range) => {
    const low = range.querySelector('[data-rfc-price-min]');
    const high = range.querySelector('[data-rfc-price-max]');
    const fields = range.nextElementSibling;
    const minField = fields?.querySelector('[data-rfc-min-field]');
    const maxField = fields?.querySelector('[data-rfc-max-field]');
    if (!low || !high || !minField || !maxField) return;
    const ceiling = Math.max(0.01, Number(high.max) || 0.01);
    const paint = () => {
      const min = Math.max(0, Math.min(ceiling, Number(low.value) || 0));
      const max = Math.max(min, Math.min(ceiling, Number(high.value)));
      range.style.setProperty('--rfc-price-min', `${min / ceiling * 100}%`);
      range.style.setProperty('--rfc-price-max', `${max / ceiling * 100}%`);
    };
    low.addEventListener('input', () => {
      if (Number(low.value) > Number(high.value)) high.value = low.value;
      minField.value = low.value;
      paint();
    });
    high.addEventListener('input', () => {
      if (Number(high.value) < Number(low.value)) low.value = high.value;
      maxField.value = high.value;
      paint();
    });
    minField.addEventListener('input', () => { low.value = minField.value || '0'; minField.setCustomValidity(''); paint(); });
    maxField.addEventListener('input', () => { high.value = maxField.value || String(ceiling); maxField.setCustomValidity(''); paint(); });
    range.closest('form')?.addEventListener('submit', (event) => {
      if (minField.value !== '' && maxField.value !== '' && Number(minField.value) > Number(maxField.value)) {
        event.preventDefault();
        maxField.setCustomValidity(maxField.dataset.rfcPriceError || 'Invalid price range.');
        maxField.reportValidity();
      }
    });
    paint();
  });

  function availableVariant() {
    return current?.variants?.find((variant) => variant.available && variant.options.every((value, index) => String(value) === String(chosen[index]))) || null;
  }
  function renderQuick() {
    if (!current || !quick) return;
    const variant = availableVariant();
    const image = quick.querySelector('.rfc__quick-image');
    const selectedImage = imageSource(variant?.featured_image) || imageSource(current.featured_image);
    image.src = selectedImage;
    image.alt = current.title || '';
    quick.querySelector('.rfc__quick-title').textContent = current.title || '';
    const price = Number(variant?.price ?? current.price ?? 0);
    const compare = Number(variant ? variant.compare_at_price : current.compare_at_price || 0);
    const compareNode = quick.querySelector('.rfc__quick-compare');
    compareNode.hidden = compare <= price;
    compareNode.textContent = compare > price ? money(compare) : '';
    quick.querySelector('.rfc__quick-current').textContent = money(price);
    const options = quick.querySelector('.rfc__quick-options');
    options.replaceChildren();
    const defaultOnly = current.variants?.length === 1 && current.variants[0]?.title === 'Default Title';
    if (!defaultOnly) (current.options || []).forEach((name, index) => {
      const field = document.createElement('fieldset');
      field.className = 'rfc__quick-option';
      const legend = document.createElement('legend');
      legend.textContent = `${name}: ${chosen[index] || ''}`;
      field.append(legend);
      const values = document.createElement('div');
      values.className = 'rfc__quick-values';
      const unique = [...new Set((current.variants || []).map((item) => String(item.options[index] || '')))];
      const normalizedName = String(name || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      const visualNames = new Set(['color','colors','colour','colours','colore','colori','couleur','couleurs','farbe','farben','cor','cores','kolor','kleur','farg','farve','culoare','цвет','色','颜色','顏色','색상','لون','renk','style','styles','stile','stili','estilo','stil','modello','model','pattern','motif','disegno','print','design']);
      const sizeNames = new Set(['size','sizes','taglia','taglie','taille','tailles','grosse','talla','tallas','tamanho','maat','maten','storlek','rozmiar','размер','サイズ','尺码','尺寸','사이즈','مقاس']);
      const isColor = visualNames.has(normalizedName) || extraVisualNames.has(normalizedName) || (index === 0 && !sizeNames.has(normalizedName));
      unique.forEach((value) => {
        if (!value) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `rfc__quick-value${isColor ? ' rfc__quick-value--image' : ''}`;
        button.dataset.rfcOption = String(index);
        button.dataset.rfcValue = value;
        button.setAttribute('aria-label', `${name}: ${value}`);
        button.setAttribute('aria-pressed', String(chosen[index] === value));
        button.disabled = !(current.variants || []).some((item) => item.available && String(item.options[index]) === value);
        if (isColor) {
          const representative = current.variants.find((item) => String(item.options[index]) === value && item.featured_image);
          const src = imageSource(representative?.featured_image) || imageSource(current.featured_image);
          if (src) { const thumb = document.createElement('img'); thumb.src = src; thumb.alt = ''; thumb.loading = 'lazy'; button.append(thumb); }
          else button.textContent = value;
          button.title = value;
        } else button.textContent = value;
        values.append(button);
      });
      field.append(values);
      options.append(field);
    });
    const add = quick.querySelector('[data-rfc-add]');
    add.disabled = busy || !variant;
    add.textContent = variant ? quick.dataset.rfcAddLabel : quick.dataset.rfcUnavailableLabel;
  }
  root.addEventListener('click', (event) => {
    const openFilter = event.target.closest('[data-rfc-open]');
    if (openFilter) { root.querySelector('.rfc__filter-dialog')?.showModal(); return; }
    const openOffer = event.target.closest('[data-rfc-offer-open]');
    if (openOffer) { document.getElementById(`RfcOffer-${openOffer.dataset.rfcOfferOpen}`)?.showModal(); return; }
    const close = event.target.closest('[data-rfc-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    const swatch = event.target.closest('[data-rfc-swatch]');
    if (swatch) {
      event.preventDefault();
      const card = swatch.closest('.rfc-card');
      const image = card?.querySelector('.rfc-card__image');
      if (image instanceof HTMLImageElement && swatch.dataset.rfcImage) { image.src = swatch.dataset.rfcImage; image.removeAttribute('srcset'); }
      card?.querySelectorAll('.rfc-card__media,.rfc-card__title').forEach((link) => { link.href = swatch.href; });
      card?.querySelectorAll('[data-rfc-swatch]').forEach((link) => link.classList.toggle('is-active', link === swatch));
      return;
    }
    const openQuick = event.target.closest('[data-rfc-quick]');
    if (openQuick && quick) {
      try { current = JSON.parse(openQuick.closest('.rfc-card').querySelector('.rfc-card__data').textContent); }
      catch { current = null; }
      if (!current) return;
      const selectedId = new URL(openQuick.closest('.rfc-card').querySelector('.rfc-card__media').href).searchParams.get('variant');
      const first = current.variants.find((variant) => String(variant.id) === selectedId && variant.available) || current.variants.find((variant) => variant.available) || current.variants[0];
      chosen = [...(first?.options || [])];
      quick.querySelector('.rfc__quick-status').textContent = '';
      renderQuick(); quick.showModal(); return;
    }
    const option = event.target.closest('[data-rfc-option]');
    if (option && current) {
      const index = Number(option.dataset.rfcOption);
      const value = option.dataset.rfcValue;
      chosen[index] = value;
      let match = availableVariant();
      if (!match) match = current.variants.find((variant) => variant.available && String(variant.options[index]) === value);
      if (match) chosen = [...match.options];
      renderQuick(); return;
    }
    const add = event.target.closest('[data-rfc-add]');
    if (add && !busy) addToCart();
  });

  async function addToCart() {
    const variant = availableVariant();
    if (!variant) return;
    busy = true; renderQuick();
    const status = quick.querySelector('.rfc__quick-status');
    status.textContent = quick.dataset.rfcAddingLabel;
    const deferred = CartLinesUpdateEvent.createPromise();
    quick.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(variant.id), quantity: 1 }], promise: deferred.promise }));
    try {
      const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId).filter(Boolean);
      const body = new FormData();
      body.append('id', String(variant.id));
      body.append('quantity', '1');
      if (sections.length) body.append('sections', sections.join(','));
      const response = await fetch(window.Theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', body, headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || quick.dataset.rfcErrorLabel);
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      quick.close();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections: result.sections, source: 'ritual-fashion-collection', itemCount: 1, productId: current.id, didError: false } });
    } catch (error) {
      status.textContent = error.message || quick.dataset.rfcErrorLabel;
      deferred.reject(error);
    } finally { busy = false; renderQuick(); }
  }
  root.querySelectorAll('dialog').forEach((dialog) => dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); }));
}
document.querySelectorAll('.rfc').forEach(init);
document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('.rfc').forEach(init));
